import { useEffect } from 'react';
import type { ExploreItem } from '../../content/expand';
import { useBookReadingStore } from '../../store/useBookReadingStore';
import { useExploreStore } from '../../store/useExploreStore';
import { recordExploreEvent } from './exploreActivity';

export function useBookReading(item: ExploreItem | undefined, childId: string | null) {
  useEffect(() => {
    if (!item || item.kind !== 'book' || !childId) return;
    let last = Date.now(), lastInput = last, running = document.visibilityState === 'visible' && document.hasFocus();
    let visited = false, unreportedMs = 0;
    const visit = () => {
      if (visited || !running) return;
      visited = true;
      useBookReadingStore.getState().visit(childId,item.id);
      useExploreStore.getState().recordOpen(item.id,'book');
      void recordExploreEvent(childId,'explore_content_opened',{ itemId:item.id,kind:'book' });
    };
    const report = () => {
      if (unreportedMs < 1000) return;
      void recordExploreEvent(childId,'explore_time_recorded',{itemId:item.id,kind:'book',seconds:Math.floor(unreportedMs/1000),measurement:'foreground-active-v1'});
      unreportedMs %= 1000;
    };
    const flush = () => {
      const now=Date.now(),end=Math.min(now,lastInput+120000);
      // A suspended tab/device must not receive a large catch-up duration.
      if (running && now-last <= 30000 && end > last) {
        useBookReadingStore.getState().addTime(childId,item.id,last,end);
        const store=useExploreStore.getState();
        store.recordProgress(item.id,'book',store.records[item.id]?.progress ?? 0,(end-last)/1000);
        unreportedMs += end-last;
        if(unreportedMs>=60000)report();
      }
      last=now;
    };
    const activity = () => { flush(); lastInput=Date.now(); };
    const visibility = () => { flush();report();running=document.visibilityState==='visible'&&document.hasFocus();last=Date.now();lastInput=last;visit(); };
    const pagehide = () => { flush();report();running=false; };
    visit();
    const timer=window.setInterval(flush,15000);
    document.addEventListener('visibilitychange',visibility);
    window.addEventListener('focus',visibility);window.addEventListener('blur',visibility);
    window.addEventListener('pagehide',pagehide);window.addEventListener('pageshow',visibility);
    for(const event of ['pointerdown','keydown','touchstart','scroll'])document.addEventListener(event,activity,{passive:true,capture:true});
    return () => {
      flush();report();window.clearInterval(timer);
      document.removeEventListener('visibilitychange',visibility);
      window.removeEventListener('focus',visibility);window.removeEventListener('blur',visibility);
      window.removeEventListener('pagehide',pagehide);window.removeEventListener('pageshow',visibility);
      for(const event of ['pointerdown','keydown','touchstart','scroll'])document.removeEventListener(event,activity,true);
    };
  },[item,childId]);
}
